import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Phone, MapPin, CheckCircle, Smile, Grid3X3, Zap, Wrench, Settings, Shield, Moon, Star, Users, Award, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { apiRequest } from '../lib/utils';
import AppointmentModal from '../components/AppointmentModal';

const HERO_IMAGE = 'https://customer-assets.emergentagent.com/job_smile-restore-coos/artifacts/8dose0j7_front%20image%20woman.jpg';

const SERVICE_ICONS = {
  'Full Dentures': Smile,
  'Partial Dentures': Grid3X3,
  'Immediate Dentures': Zap,
  'Denture Repairs': Wrench,
  'Relines & Adjustments': Settings,
  'Custom Mouth Guards': Shield,
  'Mouth Guards': Shield,
};

const BENEFITS = [
  { icon: CheckCircle, text: 'Lower overall costs' },
  { icon: CheckCircle, text: 'Faster turnaround times' },
  { icon: CheckCircle, text: 'Personalized attention' },
  { icon: CheckCircle, text: 'Precision custom fit' },
  { icon: CheckCircle, text: 'Ongoing maintenance and support' },
];

const RESTORE_ITEMS = [
  'Confidence',
  'Clear speech',
  'Proper chewing',
  'Digestive health support',
  'Facial structure support',
  'Oral hygiene maintenance',
];

const STATS = [
  { value: '40+', label: 'Years Experience' },
  { value: '10K+', label: 'Happy Patients' },
  { value: '13', label: 'Cities Served' },
  { value: '7', label: 'Services Offered' },
];

export default function HomePage() {
  const [services, setServices] = useState([]);
  const [showAppointment, setShowAppointment] = useState(false);

  useEffect(() => {
    apiRequest('/public/services').then(setServices).catch(console.error);
  }, []);

  return (
    <div className="pt-24 lg:pt-32" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-white to-orange-50/30" />
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-orange-100/40 to-transparent" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Content */}
            <div className="animate-fade-in">
              <div className="inline-flex items-center gap-2 bg-orange-100 text-orange-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Award className="w-4 h-4" />
                Oregon's Longest Standing Denturist Practice
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight tracking-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
                <span className="text-orange-600">Community Dental Lab</span>{' '}
                Oregon's Trusted Denturist Serving the Oregon Coast
              </h1>
              
              <p className="mt-6 text-lg text-slate-600 leading-relaxed max-w-xl">
                High-quality custom dentures and dental appliances delivered directly by a licensed Denturist — without unnecessary referrals.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 mt-10">
                <Button
                  onClick={() => setShowAppointment(true)}
                  className="bg-orange-600 hover:bg-orange-700 text-white rounded-full px-8 py-6 text-lg shadow-xl shadow-orange-600/20 transition-all hover:scale-105"
                  data-testid="hero-schedule-btn"
                >
                  Schedule Consultation
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <Button
                  variant="outline"
                  className="border-2 border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white rounded-full px-8 py-6 text-lg transition-all"
                  data-testid="hero-call-btn"
                >
                  <Phone className="w-5 h-5 mr-2" />
                  (541) 756-2121
                </Button>
              </div>

              {/* Trust badges */}
              <div className="flex items-center gap-6 mt-12">
                <div className="flex -space-x-3">
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="w-10 h-10 rounded-full bg-slate-200 border-2 border-white" />
                  ))}
                </div>
                <div>
                  <div className="flex items-center gap-1">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <Star key={i} className="w-4 h-4 fill-orange-500 text-orange-500" />
                    ))}
                  </div>
                  <p className="text-sm text-slate-600 mt-1">Trusted by thousands of patients</p>
                </div>
              </div>
            </div>

            {/* Image */}
            <div className="relative hidden lg:block animate-fade-in stagger-2">
              <div className="relative">
                <img
                  src={HERO_IMAGE}
                  alt="Happy patient with beautiful smile"
                  className="rounded-3xl shadow-2xl w-full aspect-[4/5] object-cover"
                />
                {/* Floating card */}
                <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-6 shadow-xl border border-slate-100 max-w-xs">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-full bg-orange-100 flex items-center justify-center">
                      <CheckCircle className="w-7 h-7 text-orange-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900">Direct Care Model</p>
                      <p className="text-sm text-slate-500">No middlemen, no referrals</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Section */}
      <section className="py-24 bg-white" data-testid="why-choose-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
                Why Choose a Denturist?
              </h2>
              <p className="mt-6 text-lg text-slate-600 leading-relaxed">
                When you choose a Denturist, you work directly with the licensed specialist who designs, fabricates, and fits your dentures. No referral chains. No unnecessary markups. No middlemen.
              </p>
              <p className="mt-4 text-slate-600">
                Patients across Coos Bay, North Bend, Bandon, Florence, and Roseburg choose direct denturist care for:
              </p>
              <ul className="mt-6 space-y-4">
                {BENEFITS.map((benefit, idx) => (
                  <li key={idx} className="flex items-center gap-3 animate-fade-in" style={{ animationDelay: `${idx * 100}ms` }}>
                    <benefit.icon className="w-6 h-6 text-orange-600 flex-shrink-0" />
                    <span className="text-slate-700 font-medium">{benefit.text}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative">
              <div className="absolute inset-0 bg-orange-100 rounded-3xl transform rotate-3" />
              <img
                src="https://images.pexels.com/photos/35438289/pexels-photo-35438289.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Dental clinic interior"
                className="relative rounded-3xl shadow-lg w-full aspect-square object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services Section - Bento Grid */}
      <section className="py-24 bg-slate-50" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
              Our Services
            </h2>
            <p className="mt-4 text-lg text-slate-600">
              Comprehensive denture and oral appliance services delivered with precision and care.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {services.map((service, idx) => {
              const Icon = SERVICE_ICONS[service.title] || Smile;
              const isLarge = idx === 0 || idx === 3;
              return (
                <Link
                  key={service.id}
                  to="/services"
                  className={`group relative overflow-hidden rounded-2xl bg-white p-8 shadow-sm border border-slate-100 transition-all hover:shadow-xl hover:-translate-y-1 ${isLarge ? 'md:col-span-2 md:row-span-2' : ''}`}
                  data-testid={`service-card-${service.title.toLowerCase().replace(/\s/g, '-')}`}
                >
                  <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-6 transition-colors ${idx === 0 ? 'bg-slate-900 text-white' : 'bg-orange-100 text-orange-600 group-hover:bg-orange-600 group-hover:text-white'}`}>
                    <Icon className="w-7 h-7" />
                  </div>
                  <h3 className={`font-semibold text-slate-900 ${isLarge ? 'text-2xl' : 'text-lg'}`} style={{ fontFamily: 'Playfair Display, serif' }}>
                    {service.title}
                  </h3>
                  <p className={`mt-3 text-slate-600 ${isLarge ? 'text-base' : 'text-sm'}`}>
                    {service.short_description || service.description.substring(0, 100)}
                  </p>
                  <div className="mt-4 flex items-center text-orange-600 text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                    Learn more <ArrowRight className="w-4 h-4 ml-2" />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Restoring More Than Smiles */}
      <section className="py-24 bg-slate-900 text-white" data-testid="restore-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
                Restoring More Than Smiles
              </h2>
              <p className="mt-6 text-lg text-slate-300 leading-relaxed">
                Properly fitted dentures restore so much more than just your smile. They transform your quality of life.
              </p>
              <div className="grid grid-cols-2 gap-4 mt-10">
                {RESTORE_ITEMS.map((item, idx) => (
                  <div
                    key={idx}
                    className="flex items-center gap-3 bg-slate-800/50 rounded-xl px-4 py-3"
                  >
                    <CheckCircle className="w-5 h-5 text-orange-500 flex-shrink-0" />
                    <span className="text-slate-200 text-sm">{item}</span>
                  </div>
                ))}
              </div>
              <Button
                onClick={() => setShowAppointment(true)}
                className="mt-10 bg-orange-600 hover:bg-orange-700 text-white rounded-full px-8 py-6 text-lg shadow-xl shadow-orange-600/30"
                data-testid="restore-cta-btn"
              >
                Start Your Transformation
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
            <div className="relative">
              <img
                src="https://customer-assets.emergentagent.com/job_smile-restore-coos/artifacts/he9bxnzf_senior%20couple.webp"
                alt="Happy senior couple smiling"
                className="rounded-3xl w-full aspect-[4/5] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent rounded-3xl" />
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-white" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {STATS.map((stat, idx) => (
              <div key={idx} className="text-center">
                <p className="text-4xl lg:text-5xl font-bold text-slate-900" style={{ fontFamily: 'Playfair Display, serif' }}>
                  {stat.value}
                </p>
                <p className="mt-2 text-slate-600">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Serving the Oregon Coast */}
      <section className="py-24 bg-slate-50" data-testid="locations-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
            Serving the Oregon Coast
          </h2>
          <p className="mt-4 text-lg text-slate-600 max-w-2xl mx-auto">
            We proudly serve patients from across the Oregon Coast and surrounding communities.
          </p>
          <div className="flex flex-wrap justify-center gap-3 mt-10">
            {['Coos Bay', 'North Bend', 'Bandon', 'Reedsport', 'Coquille', 'Myrtle Point', 'Brookings', 'Florence', 'Roseburg', 'Gold Beach', 'Lakeside', 'Charleston', 'Winchester Bay'].map((city) => (
              <span
                key={city}
                className="inline-flex items-center gap-2 bg-white px-4 py-2 rounded-full text-sm text-slate-700 border border-slate-200 hover:border-orange-300 hover:text-orange-600 transition-colors"
              >
                <MapPin className="w-4 h-4" />
                {city}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-orange-600" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
            Ready to Restore Your Smile?
          </h2>
          <p className="mt-4 text-xl text-orange-100">
            Schedule your consultation today and experience the difference of direct denturist care.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-10">
            <Button
              onClick={() => setShowAppointment(true)}
              className="bg-white text-orange-600 hover:bg-orange-50 rounded-full px-8 py-6 text-lg shadow-xl transition-all hover:scale-105"
              data-testid="bottom-cta-schedule-btn"
            >
              Schedule Consultation
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button
              variant="outline"
              className="border-2 border-white text-white hover:bg-white hover:text-orange-600 rounded-full px-8 py-6 text-lg transition-all"
              data-testid="bottom-cta-call-btn"
            >
              <Phone className="w-5 h-5 mr-2" />
              (541) 756-2121
            </Button>
          </div>
        </div>
      </section>

      <AppointmentModal open={showAppointment} onOpenChange={setShowAppointment} />
    </div>
  );
}
